import React, { Fragment, useState, useEffect, useMemo } from 'react';
import classNames from 'classnames';
import { Nav, NavItem, NavLink, TabContent, TabPane } from 'reactstrap';
import { Button } from 'reactstrap';
import { I } from '../../../common/v5/config';
import AgentGroupsForm from './agentGroups';
import AgentSkillsManager from '../../../components/agent/AgentSkillsManager';
import V5Input from '../../../components/form/V5Input';
import V5Select from '../../../components/form/V5Select';
import V5Switch from '../../../components/form/V5Switch';
import { EditorFooter, SaveButton, CancelButton, EditorHeader, editorTitleByView } from '../../../reactcomponents/Admin';
import { TableIconicButton, FormInputWithLabelRow } from '../../../reactcomponents/Form';
import AgentAvatarInput from '../../../reactcomponents/AgentAvatarInput';
import { NoFlipMultiSelect, SingleSelectDD } from '../../../reactcomponents/Dropdown';
import { formatValueToNameObj, dataURItoBlob } from '../../../common/v5/helpers';
import Feature from '../../../components/v5/Feature';
import { AgentAccessKey } from '../../../components/v5/Agent';
import { DARK_MODE_APPEARANCE } from '../../../common/v5/constants';

const AgentEditForm = ({
  hidden,
  input,
  activeId,
  view,
  isNew,
  onChangeAdminInput,
  onSave,
  onCancel,
  onDelete,
  onActivateAgent, // Add this prop for handling agent activation/deactivation
  adminStatus,
  areaList,
  availableAreas,
  langSrc,
  interfaceLangSrc,
  timeZone,
  salutationList,
  signatureList,
  availableSkills,
  proficiencyList,
  spellCheckLanguages,
  // Add adminLevelSrc prop
  adminLevelSrc,
  // Group-related props
  groupsAgentList,
  groupAgentListReady,
  onGetAgentGroups,
  onSaveAgentGroups,
  // Access key props
  accessKeyData,
  accessKeyURLCreator,
  onCreateAgentAccessKey,
  onDeleteAgentAccessKey,
  onLoadAgentAccessKeys,
  timezoneOffset,
  // Skills props
  onLoadSkills,
  onSaveAgentSkill,
  onRemoveAgentSkill,
  onUpdateAgentSkill,
  // Sysadmin access control
  isSysadmin,
  // Area-specific salutation and signature props
  selectedAreaSalutationList,
  selectedAreaSignatureList,
  areaSalutations,
  areaSignatures,
  onAddMP,
  onDeleteMP,
  onGetAreaSalutations,
  onGetAreaSignatures,
  chatbotProviders,
  features = {},
  ...props
}) => {

  const [activeTab, setActiveTab] = useState('general');
  const [showArea, setShowArea] = useState(false);
  const [showManualArea, setShowManualArea] = useState(false);
  const [showAreaSalutation, setShowAreaSalutation] = useState(false);
  const [showAreaSignature, setShowAreaSignature] = useState(false);
  const [selectedAreaForSalutation, setSelectedAreaForSalutation] = useState(0);
  const [selectedAreaForSignature, setSelectedAreaForSignature] = useState(0);
  const [selectedSalutationForArea, setSelectedSalutationForArea] = useState(0);
  const [selectedSignatureForArea, setSelectedSignatureForArea] = useState(0);

  useEffect(() => {
    const hasSavedSalutations = selectedAreaSalutationList && selectedAreaSalutationList.length > 0;
    if (hasSavedSalutations) {
      if (!input.areaSpecificSalutation) {
        onChangeAdminInput('areaSpecificSalutation', true);
      }
      // Show the add UI if we have saved data
      if (!showAreaSalutation) {
        setShowAreaSalutation(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAreaSalutationList?.length]); 

  useEffect(() => {
    const hasSavedSignatures = selectedAreaSignatureList && selectedAreaSignatureList.length > 0;
    if (hasSavedSignatures) {
      if (!input.areaSignature) {
        onChangeAdminInput('areaSignature', true);
      }
      if (!showAreaSignature) {
        setShowAreaSignature(true);
      }
    }
  }, [selectedAreaSignatureList?.length]); 

  const flattenAreaList = (areas) => {
    if (!areas) {
      return [];
    }
    
    if (typeof areas === 'object' && !Array.isArray(areas) && areas.active) {
      areas = areas.active;
    }
    
    if (!Array.isArray(areas) || areas.length === 0) {
      return [];
    }
    
    const flatAreas = [];
    areas.forEach((org) => {
      if (org.Areas && Array.isArray(org.Areas)) {
        // Nested structure: [{Areas: [{Id, Name}]}]
        org.Areas.forEach(area => {
          flatAreas.push({
            id: area.Id || area.id,
            name: area.Name || area.name,
            value: area.Name || area.name
          });
        });
      } else if (org.Id || org.id) {
        // Already flat structure: [{Id, Name}]
        flatAreas.push({
          id: org.Id || org.id,
          name: org.Name || org.name,
          value: org.Name || org.name
        });
      }
    });
    
    return flatAreas;
  };
    const flattenedAreaList = useMemo(() => {
    // Try availableAreas first (from API response)
    let areasToFlatten = availableAreas;
    
    // If availableAreas is empty or not available, use areaList from Redux
    if (!areasToFlatten || (Array.isArray(areasToFlatten) && areasToFlatten.length === 0)) {
      areasToFlatten = areaList;
    }
    
    const flattened = flattenAreaList(areasToFlatten);
    return flattened;
  }, [areaList, availableAreas]);

  const chatbotProvidersList = useMemo(() => {
    const raw = chatbotProviders;
    if (raw == null) return [];
    if (Array.isArray(raw)) return raw;
    if (typeof raw === 'string') {
      try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return [];
  }, [chatbotProviders]);

  const conversationBotEnabled = useMemo(() => {
    if (input.automation !== undefined && input.automation !== null) {
      return !!input.automation;
    }
    const b = input.automatedConversationBot;
    if (b === true || b === 1) return true;
    if (typeof b === 'string') {
      const s = b.toLowerCase();
      return s === 'true' || s === '1';
    }
    return false;
  }, [input.automation, input.automatedConversationBot]);

  const showAutoAnswerFeature =
    !!features['machine-learning.auto-answer'] ||
    (!!features['amazon-comprehend'] && !!features['machine-learning.suggest-answer']);
  
  // Load skills data when component mounts or when editing an existing agent
  useEffect(() => {
    
    if (onLoadSkills && !isNew && activeId > 0) {
      try {
        onLoadSkills();
      } catch (error) {
        console.error('🔍 Error calling onLoadSkills:', error);
      }
    } else {
      console.log('🔍 Skills data loading skipped:', {
        onLoadSkills: !!onLoadSkills,
        isNew,
        activeId
      });
    }
  }, [onLoadSkills, isNew, activeId]);
  
  const handleTextInputChange = (e) => {
    let field = e.target.name, value = e.target.value;
    
    // Special handling for numeric fields
    if (['allowedSimultaneousChatSessions', 'previewDelayTime', 'errandListSize'].includes(field)) {
      value = parseInt(value) || 0;
    }
    
    onChangeAdminInput(field, value);
  };

  const handleCheckboxChange = (field, value) => {
    if (field === 'automation' || field === 'automatedConversationBot') {
      const on = !!value;
      onChangeAdminInput('automatedConversationBot', on);
      onChangeAdminInput('automation', on);
      return;
    }

    onChangeAdminInput(field, value);
    
    // When area-specific salutation toggle is turned ON, show the add UI
    if (field === 'areaSpecificSalutation' && value) {
      setShowAreaSalutation(true);
    } else if (field === 'areaSpecificSalutation' && !value) {
      // When turned OFF, hide the add UI and reset selections
      setShowAreaSalutation(false);
      setSelectedAreaForSalutation(0);
      setSelectedSalutationForArea(0);
    }
    
    // When area-specific signature toggle is turned ON, show the add UI
    if (field === 'areaSignature' && value) {
      setShowAreaSignature(true);
    } else if (field === 'areaSignature' && !value) {
      // When turned OFF, hide the add UI and reset selections
      setShowAreaSignature(false);
      setSelectedAreaForSignature(0);
      setSelectedSignatureForArea(0);
    }
  };

  const handleNestedCheckboxChange = (parentField, childField, value) => {
    const currentParent = input[parentField] || {};
    const updatedParent = {
      ...currentParent,
      [childField]: value
    };
    onChangeAdminInput(parentField, updatedParent);
  };

  const handleSelectChange = (value, field) => {
    
    // Convert adminLevel to integer to match backend expectations
    if (field === 'adminLevel') {
      value = parseInt(value) || 0;
    }
    if (field === 'automatedConversationBotProvider') {
      value = parseInt(value, 10);
      if (Number.isNaN(value)) value = 0;
    }
    
    onChangeAdminInput(field, value);
  };

  const handleSkillsChange = (skills) => {
    onChangeAdminInput('skills', skills);
  };

  // Area selection handlers
  const handleSelectArea = (val) => {
    onChangeAdminInput('preferredAreaAllErrands', val);
  };

  const handleToggleArea = () => {
    setShowArea(!showArea);
  };

  const handleSelectManualArea = (val) => {
    onChangeAdminInput('preferredAreaManualErrand', val);
  };

  const handleToggleManualArea = () => {
    setShowManualArea(!showManualArea);
  };

  const handleSave = (...args) => {
    // Handle different parameter patterns from HOCs
    const event = args.find(arg => arg && typeof arg.preventDefault === 'function');
    if (event) {
      event.preventDefault();
    }
    onSave(input);
  };

  const toggle = (tab) => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  // Avatar handlers
  const handleAvatarChange = (file) => {
    // Compress and resize image like the old Avatar component
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        // Create canvas to resize/compress image
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        // Set target dimensions (same as old implementation)
        const maxWidth = 160;
        const maxHeight = 120;
        
        // Calculate new dimensions maintaining aspect ratio
        let { width, height } = img;
        if (width > height) {
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = (width * maxHeight) / height;
            height = maxHeight;
          }
        }
        
        canvas.width = width;
        canvas.height = height;
        
        // Draw and compress image
        ctx.drawImage(img, 0, 0, width, height);
        const compressedDataURL = canvas.toDataURL('image/jpeg', 0.8); // 80% quality
        
        onChangeAdminInput('avatarPreview', compressedDataURL);
        // Upload avatar immediately like old implementation
        handleAvatarUpload(compressedDataURL);
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  };

  const handleAvatarUpload = (preview) => {
    const fd = new FormData();
    let i = 0;
    let rand = Math.floor(Math.random() * 6) + '' + i + '' + Math.floor('' + new Date() / 1000);
    
    const blob = dataURItoBlob(preview);
    fd.append("uploadedAvatar", blob);
    fd.append("localName", rand);
    
    // Use jQuery ajax with same settings as RESTful postMultipart
    $.ajax({
      url: window.webserverRoot + "admin/agent/uploadAgentAvatar",
      data: fd,
      cache: false,
      contentType: false,
      processData: false,
      type: 'POST',
      success: function(rd) {
        // Store the uploaded avatar info for saving
        onChangeAdminInput('avatarLocalName', rd.id);
        onChangeAdminInput('avatarContentType', rd.value);
        console.log('Avatar uploaded successfully:', rd);
      },
      error: function(xhr, status, error) {
        console.error('Avatar upload failed:', error, xhr.responseText);
        // Optionally show user feedback
      }
    });
  };

  const handleAvatarRemove = () => {
    // Clear all avatar-related fields
    onChangeAdminInput('avatarPreview', '');
    onChangeAdminInput('avatarUrl', '');
    onChangeAdminInput('avatar', '');
    onChangeAdminInput('avatarLocalName', '');
    onChangeAdminInput('avatarContentType', '');
    
    // If editing existing agent, call remove API like old implementation
    if (!isNew && activeId > 0) {
      const fd = new FormData();
      fd.append("id", activeId);
      $.ajax({
        url: window.webserverRoot + "admin/agent/removeAgentAvatar",
        data: fd,
        cache: false,
        contentType: false,
        processData: false,
        type: 'POST',
        success: function(rd) {
          console.log('Avatar removed successfully:', rd);
        },
        error: function(xhr, status, error) {
          console.error('Avatar removal failed:', error, xhr.responseText);
        }
      });
    }
  };

  // Remove hardcoded adminLevelOptions and use API data instead
  const adminLevelOptions = formatValueToNameObj(adminLevelSrc) || [
    { id: 3, name: I('Agent') },           // Fallback only if API data is missing
    { id: 4, name: I('Observer') },        // Fallback only if API data is missing
    { id: 5, name: I('Administrator') }    // Fallback only if API data is missing
  ];
  
  // Try different data sources for interface language
  const interfaceLanguageOptions = formatValueToNameObj(interfaceLangSrc) || formatValueToNameObj(langSrc) || [];
  
  // Remove hardcoded defaultTimezones and use API data only
  const timezoneFormatted = formatValueToNameObj(timeZone);
  const timezoneOptions = timezoneFormatted || [];

  return (
    <div className="agent-edit-container" hidden={hidden}>
      {/* Header - Outside the shadowed form area */}
      <div className='admin-heading edit'>
        <div className='col-split-left'>
          <span className='admin-icons left mb-0'>
            <i className={classNames({ 'icon-edit': !isNew }, { 'icon-add': isNew })} />
          </span>
          <h5 className='d-inline'>{isNew ? I('Create Agent') : I('Edit Agent')}</h5>
        </div>
        <div className='col-split-right'>
          <TableIconicButton
            type='button'
            className='btn bg-transparent'
            iconClass='icon-times'
            title={I('Close form')}
            onClick={onCancel}
          />
        </div>
      </div>
      
      {/* Form content - Inside the shadowed area */}
      <div className="edit-admin-form agent-v5-form">
      <form id="agentEdit" className="admin-one-form">
      {/* Agent Overview Section */}
      <div className="agent-overview">
        <AgentAvatarInput
          currentAvatar={input.avatarPreview}
          onAvatarChange={handleAvatarChange}
          onAvatarRemove={handleAvatarRemove}
          />
        <div className="agent-basic-info">
          <div className="input-wrapper">
            <V5Input
              id="userName"
              label={I('Name')}
              name="userName"
              value={input.userName || ''}
              onChange={handleTextInputChange}
              required
              placeholder={I('Enter agent name')}
            />
            <V5Input
              id="email"
              label={I('Email')}
              name="email"
              value={input.email || ''}
              onChange={handleTextInputChange}
              required
              type="email"
              placeholder={I('Enter email address')}
            />
          </div>
        </div>
      </div>

      {/* Tabbed Section */}
      <div className="agent-tabs">
        <Nav tabs>
          <NavItem>
            <NavLink
              className={classNames({ active: activeTab === 'general' })}
              onClick={() => toggle('general')}
            >
              <i className="icon-user"></i>
              {I('General')}
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={classNames({ active: activeTab === 'groups' })}
              onClick={() => toggle('groups')}
            >
              <i className="icon-group"></i>
              {I('Groups')}
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={classNames({ active: activeTab === 'preferences' })}
              onClick={() => toggle('preferences')}
            >
              <i className="icon-settings"></i>
              {I('Preferences')}
            </NavLink>
          </NavItem>
        </Nav>

        <TabContent activeTab={activeTab}>
          {/* General Tab */}
          <TabPane tabId="general">
            <div className="tab-content-wrapper">
              <div className="form-section">
                <div className="v5-section-header">{I('General Information')}</div>
                
              <V5Input
                id="loginName"
                label={I('Login')}
                name="loginName"
                value={input.loginName || ''}
                onChange={handleTextInputChange}
                required
                placeholder={I('Enter login name')}
              />
                
                <V5Input
                  id="password"
                  label={I('Password')}
                  name="password"
                  type="password"
                  value={input.password || ''}
                  onChange={handleTextInputChange}
                  required
                  placeholder={I('Enter password')}
                  helperText={I('Minimum 8 characters, include uppercase and number')}
              />
              
              <V5Select
                id="adminLevel"
                label={I('Administration Level')}
                name="adminLevel"
                value={input.adminLevel || 0}
                onChange={handleSelectChange}
                options={adminLevelOptions}
                placeholder={I('Select admin level')}
              />

              <V5Input
                id="externalID"
                label={I('External ID')}
                name="externalID"
                value={input.externalID || ''}
                onChange={handleTextInputChange}
                placeholder={I('Enter external ID')}
              />

              {/* Mark as Cention User - Sysadmin only */}
              {isSysadmin && (
                <V5Switch
                  id="markAsCentionUser"
                  label={I('Mark as Cention User')}
                  name="markAsCentionUser"
                  checked={input.markAsCentionUser || false}
                  onChange={(checked) => handleCheckboxChange('markAsCentionUser', checked)}
                />
              )}
              </div>

              {/* SIP Configuration Section */}
              <Feature tag="sip-enabled">
                <div className="form-section">
                  <div className="v5-section-header">{I('SIP Configuration')}</div>
                  
                  <V5Input
                    id="sipExtensionId"
                    label={I('SIP Extension ID')}
                    name="sipExtensionId"
                    value={input.sipExtensionId || ''}
                    onChange={handleTextInputChange}
                    placeholder={I('Enter SIP extension ID')}
                  />
                  
                  <V5Input
                    id="sipDeviceId"
                    label={I('SIP Device ID')}
                    name="sipDeviceId"
                    value={input.sipDeviceId || ''}
                    onChange={handleTextInputChange}
                    placeholder={I('Enter SIP device ID')}
                  />
                  
                  <V5Input
                    id="sipPassword"
                    label={I('SIP Password')}
                    name="sipPassword"
                    type="password"
                    value={input.sipPassword || ''}
                    onChange={handleTextInputChange}
                    placeholder={I('Enter SIP password')}
                  />
                  
                  <V5Input
                    id="selectedSipRingOutput"
                    label={I('SIP Ring Output Device')}
                    name="selectedSipRingOutput"
                    value={input.selectedSipRingOutput || ''}
                    onChange={handleTextInputChange}
                    placeholder={I('Enter ring output device')}
                  />
                </div>
              </Feature>

              {/* System Integration Section */}
              {(window.features && (window.features['solidusEnabled'] || window.features['bright-integration'])) && (
              <div className="form-section">
                <div className="v5-section-header">{I('System Integration')}</div>
                
                <Feature tag="solidusEnabled">
                  <V5Input
                    id="solidusAgentId"
                    label={I('Solidus Agent ID')}
                    name="solidusAgentId"
                    type="number"
                    value={input.solidusAgentId || 0}
                    onChange={handleTextInputChange}
                    placeholder={I('Enter Solidus agent ID')}
                  />
                </Feature>
                
                <Feature tag="bright-integration">
                  <V5Input
                    id="brightAgentReferenceCode"
                    label={I('Bright Agent Reference Code')}
                    name="brightAgentReferenceCode"
                    value={input.brightAgentReferenceCode || ''}
                    onChange={handleTextInputChange}
                    placeholder={I('Enter Bright reference code')}
                  />
                </Feature>
              </div>
              )}

              {/* Skills Section */}
              {!isNew && (
                <div className="form-section">
                  <div className="v5-section-header">{I('Skills')}</div>
                  {availableSkills && proficiencyList && onLoadSkills && onSaveAgentSkill && onRemoveAgentSkill && onUpdateAgentSkill ? (
                    <AgentSkillsManager
                      skills={input.skills || []}
                      onSkillsChange={handleSkillsChange}
                      availableSkills={availableSkills}
                      proficiencyList={proficiencyList}
                      onSaveAgentSkill={onSaveAgentSkill}
                      onRemoveAgentSkill={onRemoveAgentSkill}
                      onUpdateAgentSkill={onUpdateAgentSkill}
                      agentId={activeId}
                    />
                  ) : (
                    <div className="debug-error-message">
                      <strong>Skills Component Not Loading:</strong><br/>
                      Feature Flag Enabled: {window.cflag && window.cflag.IsActive("2023-09-29.CEN-1599.skills.admin.page") ? '✅' : '❌'}<br/>
                      availableSkills: {availableSkills ? `✅ ${availableSkills.length} items` : '❌'}<br/>
                      proficiencyList: {proficiencyList ? `✅ ${proficiencyList.length} items` : '❌'}<br/>
                      onLoadSkills: {onLoadSkills ? '✅' : '❌'}<br/>
                      onSaveAgentSkill: {onSaveAgentSkill ? '✅' : '❌'}<br/>
                      onRemoveAgentSkill: {onRemoveAgentSkill ? '✅' : '❌'}<br/>
                      onUpdateAgentSkill: {onUpdateAgentSkill ? '✅' : '❌'}<br/>
                      activeId: {activeId}<br/>
                      isNew: {isNew ? 'true' : 'false'}
                    </div>
                  )}
                </div>
              )}
            </div>
          </TabPane>

          {/* Groups Tab */}
          <TabPane tabId="groups">
            <div className="tab-content-wrapper">
              <AgentGroupsForm
                activeId={activeId}
                adminStatus={adminStatus}
                groupsAgentList={groupsAgentList}
                groupAgentListReady={groupAgentListReady}
                onGetAgentGroups={onGetAgentGroups}
                onSaveAgentGroups={onSaveAgentGroups}
                onCancel={onCancel}
              />
            </div>
          </TabPane>

          {/* Preferences Tab */}
          <TabPane tabId="preferences">
            <div className="tab-content-wrapper">
              
              {/* Knowledge Base & Suggestions */}
              <Feature tag="cention-library">
                <Feature tag="alternative-answers">
                  <div className="form-section">
                    <div className="v5-section-header">{I('Knowledge Base & Suggestions')}</div>
                    
                    <V5Switch
                      id="autoInsertKnowledgeBase"
                      label={I('Automatically insert alternative answer')}
                      name="autoInsertKnowledgeBase"
                      checked={input.autoInsertKnowledgeBase || false}
                      onChange={(checked) => handleCheckboxChange('autoInsertKnowledgeBase', checked)}
                    />
                    
                    <V5Switch
                      id="autoInsertSuggestions"
                      label={I('Auto pickup suggested answer')}
                      name="autoInsertSuggestions"
                      checked={input.autoInsertSuggestions || false}
                      onChange={(checked) => handleCheckboxChange('autoInsertSuggestions', checked)}
                    />
                  </div>
                </Feature>
              </Feature>

              {/* Hotkeys Section */}
              <div className="form-section">
                <div className="v5-section-header">{I('Hotkeys')}</div>
                
                <V5Switch
                  id="useHotkeys"
                  label={I('Use hotkeys')}
                  name="useHotkeys"
                  checked={input.useHotkeys || false}
                  onChange={(checked) => handleCheckboxChange('useHotkeys', checked)}
                />

                {input.useHotkeys && (
                  <>
                    <V5Switch
                      id="activateHotkeys"
                      label={I('Activate hotkeys')}
                      name="activateHotkeys"
                      checked={input.activateHotkeys || false}
                      onChange={(checked) => handleCheckboxChange('activateHotkeys', checked)}
                    />
                    
                    <V5Switch
                      id="showHotkeysMarkers"
                      label={I('Show hotkeys markers')}
                      name="showHotkeysMarkers"
                      checked={input.showHotkeysMarkers || false}
                      onChange={(checked) => handleCheckboxChange('showHotkeysMarkers', checked)}
                    />

                    <div className="modifiers-group">
                      <div className="v5-form-label">{I('Modifiers')}</div>
                      <div className="modifier-switches">
                        <V5Switch
                          id="hotkeysModifiersCtrl"
                          label={I('Ctrl')}
                          name="hotkeysModifiers.ctrl"
                          checked={input.hotkeysModifiers?.ctrl || false}
                          onChange={(checked) => handleNestedCheckboxChange('hotkeysModifiers', 'ctrl', checked)}
                        />
                        <V5Switch
                          id="hotkeysModifiersAlt"
                          label={I('Alt')}
                          name="hotkeysModifiers.alt"
                          checked={input.hotkeysModifiers?.alt || false}
                          onChange={(checked) => handleNestedCheckboxChange('hotkeysModifiers', 'alt', checked)}
                        />
                        <V5Switch
                          id="hotkeysModifiersShift"
                          label={I('Shift')}
                          name="hotkeysModifiers.shift"
                          checked={input.hotkeysModifiers?.shift || false}
                          onChange={(checked) => handleNestedCheckboxChange('hotkeysModifiers', 'shift', checked)}
                        />
                        <V5Switch
                          id="hotkeysModifiersMeta"
                          label={I('Meta')}
                          name="hotkeysModifiers.meta"
                          checked={input.hotkeysModifiers?.meta || false}
                          onChange={(checked) => handleNestedCheckboxChange('hotkeysModifiers', 'meta', checked)}
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Preview Section */}
              <Feature tag="cention-workflow">
                <div className="form-section">
                  <div className="v5-section-header">{I('Preview')}</div>
                  
                  <V5Switch
                    id="showErrandPreview"
                    label={I('Show errand preview')}
                    name="showErrandPreview"
                    checked={input.showErrandPreview || false}
                    onChange={(checked) => handleCheckboxChange('showErrandPreview', checked)}
                  />

                  {input.showErrandPreview && (
                    <V5Select
                      id="previewDelayTime"
                      label={I('Preview delay time')}
                      name="previewDelayTime"
                      value={input.previewDelayTime || 0}
                      onChange={handleSelectChange}
                      options={input.previewDelayOptions || [
                        { id: 0, name: I('Instant') },
                        { id: 1000, name: I('1 second') },
                        { id: 2000, name: I('2 seconds') },
                        { id: 3000, name: I('3 seconds') },
                        { id: 5000, name: I('5 seconds') }
                      ]}
                    />
                  )}
                </div>
              </Feature>

              {/* Errand List Section */}
              <div className="form-section">
                <div className="v5-section-header">{I('Errand List')}</div>
                
                <V5Select
                  id="errandListSize"
                  label={I('Errand list size')}
                  name="errandListSize"
                  value={input.errandListSize || 25}
                  onChange={handleSelectChange}
                  options={input.errandListSizeOptions || [
                    { id: 10, name: '10' },
                    { id: 25, name: '25' },
                    { id: 50, name: '50' },
                    { id: 100, name: '100' }
                  ]}
                />
                
                <V5Select
                  id="preferredSpellCheckLanguage"
                  label={I('Preferred spell check language')}
                  name="preferredSpellCheckLanguage"
                  value={input.preferredSpellCheckLanguage || 1}
                  onChange={handleSelectChange}
                  options={formatValueToNameObj(spellCheckLanguages || langSrc)}
                />
                
                <V5Switch
                  id="shortHistory"
                  label={I('Sort History Ascending')}
                  name="shortHistory"
                  checked={input.shortHistory || false}
                  onChange={(checked) => handleCheckboxChange('shortHistory', checked)}
                />
              </div>

              {/* Area Preferences Section */}
              <div className="form-section">
                <div className="v5-section-header">{I('Area Preferences')}</div>
                
                <FormInputWithLabelRow label={I('Preferred area in all errands')}>
                  <NoFlipMultiSelect
                    flip={true}
                    id="agent-areas-v5"
                    title={I("Select areas")}
                    className="popup-multi-select"
                    overrideTitle={true}
                    groupSelect={true}
                    nested={{key: 'Areas'}}
                    data={availableAreas}
                    idFields={{id: "Id", name: "Name"}}
                    selected={input.preferredAreaAllErrands || []}
                    onSelect={handleSelectArea}
                    onToggle={handleToggleArea}
                    show={showArea}
                    selectAll={true}
                    selectNone={true}
                  />
                </FormInputWithLabelRow>
                
                <FormInputWithLabelRow label={I('Preferred area in manual errand')}>
                  <SingleSelectDD
                    id="manual-errand-areas-v5"
                    textNoItemSelected={I("Select area")}
                    className="popup-multi-select"
                    nested={{key: 'Areas'}}
                    data={availableAreas}
                    idFields={{id: "Id", name: "Name"}}
                    selected={input.preferredAreaManualErrand}
                    onSelect={handleSelectManualArea}
                    onToggle={handleToggleManualArea}
                    show={showManualArea}
                />
                </FormInputWithLabelRow>
              </div>

              {/* Salutation and Signature Section */}
              <Feature tag="agent.hide-personalisation-preview">
                <div className="form-section">
                  <div className="v5-section-header">{I('Salutation and Signature Preferences')}</div>
                  
                  <V5Switch
                    id="showSalutationPreview"
                    label={I('Show salutation preview')}
                    name="showSalutationPreview"
                    checked={input.showSalutationPreview || false}
                    onChange={(checked) => handleCheckboxChange('showSalutationPreview', checked)}
                />

                  <V5Switch
                    id="showSignaturePreview"
                    label={I('Show signature preview')}
                    name="showSignaturePreview"
                    checked={input.showSignaturePreview || false}
                    onChange={(checked) => handleCheckboxChange('showSignaturePreview', checked)}
                  />
                  
                  <V5Select
                    id="preferredSalutation"
                    label={I('Preferred salutation')}
                    name="preferredSalutation"
                    value={input.preferredSalutation || 0}
                    onChange={handleSelectChange}
                    options={formatValueToNameObj(salutationList)}
                  />
                  
                  <V5Select
                    id="preferredSignature"
                    label={I('Preferred signature')}
                    name="preferredSignature"
                    value={input.preferredSignature || 0}
                    onChange={handleSelectChange}
                    options={formatValueToNameObj(signatureList)}
                  />
                  
                  <V5Input
                    id="signatureName"
                    label={I('Signature Name')}
                    name="signatureName"
                    value={input.signatureName || ''}
                    onChange={handleTextInputChange}
                    placeholder={I('Enter signature name')}
                  />
                  
                  <V5Switch
                    id="areaSpecificSalutation"
                    label={I('Area specific salutation')}
                    name="areaSpecificSalutation"
                    checked={input.areaSpecificSalutation || false}
                    onChange={(checked) => handleCheckboxChange('areaSpecificSalutation', checked)}
                  />

                  {/* Show existing area-specific salutations */}
                  {selectedAreaSalutationList && selectedAreaSalutationList.length > 0 && (
                    <div className="area-salutation-list" style={{ marginTop: '10px', marginBottom: '10px' }}>
                      <table className="table" style={{ fontSize: '12px' }}>
                        <thead>
                          <tr>
                            <th>{I('Area')}</th>
                            <th>{I('Salutation')}</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedAreaSalutationList.map((item) => (
                            <tr key={item.id}>
                              <td>{item.areaName || item.area_name || ''}</td>
                              <td>{item.personalisationName || item.personalisation_name || ''}</td>
                              <td>
                                <Button
                                  type="button"
                                  size="sm"
                                  color="danger"
                                  onClick={() => onDeleteMP && onDeleteMP(item.id, 1)}
                                  style={{ padding: '2px 8px', fontSize: '11px' }}
                                >
                                  ×
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Add new area-specific salutation - shown when toggle is ON */}
                  {input.areaSpecificSalutation && (
                    <div className="sub-label-row" style={{ marginTop: '10px', padding: '10px', border: '1px solid #ddd', borderRadius: '4px' }}>
                      <V5Select
                        id="areaSalutationArea"
                        label={I('Choose Area')}
                        name="areaSalutationArea"
                        value={selectedAreaForSalutation || 0}
                        onChange={(value) => {
                          setSelectedAreaForSalutation(value);
                          setSelectedSalutationForArea(0); // Reset salutation selection when area changes
                          if (value && value > 0 && onGetAreaSalutations) {
                            onGetAreaSalutations(value);
                          }
                        }}
                        options={flattenedAreaList.length > 0 ? formatValueToNameObj(flattenedAreaList, 'id', 'name') : []}
                      />
                      
                      {selectedAreaForSalutation > 0 && (
                        <>
                          <V5Select
                            key={`area-salutation-${selectedAreaForSalutation}-${areaSalutations?.length || 0}`}
                            id="areaSalutationSalutation"
                            label={I('Choose Salutation')}
                            name="areaSalutationSalutation"
                            value={selectedSalutationForArea || 0}
                            onChange={(value) => setSelectedSalutationForArea(value)}
                            options={(() => {
                              // Use area-specific salutations if available, otherwise fall back to all salutations
                              const salutationsToUse = (areaSalutations && areaSalutations.length > 0) 
                                ? areaSalutations 
                                : salutationList;
                              const formattedOptions = formatValueToNameObj(salutationsToUse);
                              console.log('🔵 [agentEdit] Area salutation dropdown:', {
                                selectedArea: selectedAreaForSalutation,
                                areaSalutations: areaSalutations,
                                areaSalutationsLength: areaSalutations?.length || 0,
                                salutationList: salutationList,
                                salutationListLength: salutationList?.length || 0,
                                salutationsToUse: salutationsToUse,
                                formattedOptions: formattedOptions,
                                formattedOptionsLength: formattedOptions?.length || 0
                              });
                              return formattedOptions;
                            })()}
                          />
                          <Button
                            type="button"
                            color="success"
                            size="sm"
                            onClick={() => {
                              if (selectedAreaForSalutation > 0 && selectedSalutationForArea > 0 && onAddMP) {
                                onAddMP(selectedSalutationForArea, 1, selectedAreaForSalutation);
                                // Reset selections but keep UI open (toggle controls visibility)
                                setSelectedAreaForSalutation(0);
                                setSelectedSalutationForArea(0);
                              }
                            }}
                            disabled={!selectedAreaForSalutation || !selectedSalutationForArea}
                            style={{ marginTop: '10px' }}
                          >
                            +
                          </Button>
                        </>
                      )}
                    </div>
                  )}
                  
                  <V5Switch
                    id="areaSignature"
                    label={I('Area signature')}
                    name="areaSignature"
                    checked={input.areaSignature || false}
                    onChange={(checked) => handleCheckboxChange('areaSignature', checked)}
                  />

                  {/* Show existing area-specific signatures */}
                  {selectedAreaSignatureList && selectedAreaSignatureList.length > 0 && (
                    <div className="area-signature-list" style={{ marginTop: '10px', marginBottom: '10px' }}>
                      <table className="table" style={{ fontSize: '12px' }}>
                        <thead>
                          <tr>
                            <th>{I('Area')}</th>
                            <th>{I('Signature')}</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedAreaSignatureList.map((item) => (
                            <tr key={item.id}>
                              <td>{item.areaName || item.area_name || ''}</td>
                              <td>{item.personalisationName || item.personalisation_name || ''}</td>
                              <td>
                                <Button
                                  type="button"
                                  size="sm"
                                  color="danger"
                                  onClick={() => onDeleteMP && onDeleteMP(item.id, 2)}
                                  style={{ padding: '2px 8px', fontSize: '11px' }}
                                >
                                  ×
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Add new area-specific signature - shown when toggle is ON */}
                  {input.areaSignature && (
                    <div className="sub-label-row" style={{ marginTop: '10px', padding: '10px', border: '1px solid #ddd', borderRadius: '4px' }}>
                      <V5Select
                        id="areaSignatureArea"
                        label={I('Choose Area')}
                        name="areaSignatureArea"
                        value={selectedAreaForSignature || 0}
                        onChange={(value) => {
                          setSelectedAreaForSignature(value);
                          setSelectedSignatureForArea(0); // Reset signature selection when area changes
                          if (value && value > 0 && onGetAreaSignatures) {
                            onGetAreaSignatures(value);
                          }
                        }}
                        options={flattenedAreaList.length > 0 ? formatValueToNameObj(flattenedAreaList, 'id', 'name') : []}
                      />
                      
                      {selectedAreaForSignature > 0 && (
                        <>
                          <V5Select
                            id="areaSignatureSignature"
                            label={I('Choose Signature')}
                            name="areaSignatureSignature"
                            value={selectedSignatureForArea || 0}
                            onChange={(value) => setSelectedSignatureForArea(value)}
                            options={(() => {
                              // Use area-specific signatures if available, otherwise fall back to all signatures
                              const signaturesToUse = (areaSignatures && areaSignatures.length > 0) 
                                ? areaSignatures 
                                : signatureList;
                              return formatValueToNameObj(signaturesToUse);
                            })()}
                          />
                          <Button
                            type="button"
                            color="success"
                            size="sm"
                            onClick={() => {
                              if (selectedAreaForSignature > 0 && selectedSignatureForArea > 0 && onAddMP) {
                                onAddMP(selectedSignatureForArea, 2, selectedAreaForSignature);
                                setSelectedAreaForSignature(0);
                                setSelectedSignatureForArea(0);
                              }
                            }}
                            disabled={!selectedAreaForSignature || !selectedSignatureForArea}
                            style={{ marginTop: '10px' }}
                          >
                            +
                          </Button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </Feature>

              {/* Chat Preferences Section */}
              <Feature tag="chat">
                <div className="form-section">
                  <div className="v5-section-header">{I('Chat Preferences')}</div>

                <V5Input
                    id="chatAlias"
                    label={I('Chat alias')}
                    name="chatAlias"
                    value={input.chatAlias || ''}
                  onChange={handleTextInputChange}
                    placeholder={I('Enter chat display name')}
                />
                  
                  <V5Switch
                    id="playSoundNewChatMessage"
                    label={I('Play sound for new chat message')}
                    name="playSoundNewChatMessage"
                    checked={input.playSoundNewChatMessage || false}
                    onChange={(checked) => handleCheckboxChange('playSoundNewChatMessage', checked)}
                />

                <V5Input
                    id="allowedSimultaneousChatSessions"
                    label={I('Number of allowed simultaneous chat sessions')}
                    name="allowedSimultaneousChatSessions"
                    type="number"
                    value={input.allowedSimultaneousChatSessions || 5}
                  onChange={handleTextInputChange}
                    min="1"
                    max="20"
                />

                  <V5Select
                    id="chatMessagePreview"
                    label={I('Chat message preview')}
                    name="chatMessagePreview"
                    value={input.chatMessagePreview || 0}
                    onChange={handleSelectChange}
                    options={input.chatMessagePreviewOptions || [
                      { id: 0, name: I('Disabled') },
                      { id: 1, name: I('Enabled') },
                      { id: 2, name: I('Advanced') }
                    ]}
                />
                </div>
              </Feature>

              {/* Advanced Preview Settings */}
              <Feature tag="cention-workflow">
                <div className="form-section">
                  <div className="v5-section-header">{I('Advanced Preview Settings')}</div>
                  
                  <V5Select
                    id="activatePreview"
                    label={I('Activate Preview')}
                    name="activatePreview"
                    value={input.activatePreview || 0}
                    onChange={handleSelectChange}
                    options={input.activatePreviewOptions || [
                      { id: 0, name: I('Disabled') },
                      { id: 1, name: I('Instant') },
                      { id: 2, name: I('With Delay') }
                    ]}
                  />
                  
                  {input.activatePreview === 2 && (
                    <V5Select
                      id="previewDelayTime"
                      label={I('Preview Delay Time')}
                      name="previewDelayTime"
                      value={input.previewDelayTime || 0}
                      onChange={handleSelectChange}
                      options={input.previewDelayOptions || [
                        { id: 0, name: I('Instant') },
                        { id: 1000, name: I('1 second') },
                        { id: 2000, name: I('2 seconds') },
                        { id: 3000, name: I('3 seconds') },
                        { id: 5000, name: I('5 seconds') }
                      ]}
                    />
                  )}
                  
                  <V5Switch
                    id="inboxPreview"
                    label={I('Inbox Preview')}
                    name="inboxPreview"
                    checked={input.inboxPreview || false}
                    onChange={(checked) => handleCheckboxChange('inboxPreview', checked)}
                  />
                  
                  <V5Switch
                    id="inboxPreviewShowInstantly"
                    label={I('Show Preview Instantly')}
                    name="inboxPreviewShowInstantly"
                    checked={input.inboxPreviewShowInstantly || false}
                    onChange={(checked) => handleCheckboxChange('inboxPreviewShowInstantly', checked)}
                  />
                  
                  <V5Switch
                    id="previewSummary"
                    label={I('Preview Summary')}
                    name="previewSummary"
                    checked={input.previewSummary || false}
                    onChange={(checked) => handleCheckboxChange('previewSummary', checked)}
                  />
                </div>
              </Feature>

              {/* Advanced Preferences Section */}
              <div className="form-section">
                <div className="v5-section-header">{I('Advanced Preferences')}</div>
                
                <V5Switch
                  id="showInformationAfterAction"
                  label={I('Show Information After Action')}
                  name="showInformationAfterAction"
                  checked={input.showInformationAfterAction || false}
                  onChange={(checked) => handleCheckboxChange('showInformationAfterAction', checked)}
                />
                
                <V5Switch
                  id="showOnlyWarnings"
                  label={I('Show Only Warnings')}
                  name="showOnlyWarnings"
                  checked={input.showOnlyWarnings || false}
                  onChange={(checked) => handleCheckboxChange('showOnlyWarnings', checked)}
                />
                

              </div>

              {/* Enhanced Notification Section */}
              <div className="form-section">
                <div className="v5-section-header">{I('Notification Preferences')}</div>
                
                <V5Switch
                  id="notification"
                  label={I('Blue Bubble Notifications')}
                  name="notification"
                  checked={input.notification !== false}
                  onChange={(checked) => handleCheckboxChange('notification', checked)}
                />
                
                <V5Switch
                  id="browserOsNotification"
                  label={I('Browser OS Notifications (overrides Blue Bubble)')}
                  name="browserOsNotification"
                  checked={input.browserOsNotification || false}
                  onChange={(checked) => handleCheckboxChange('browserOsNotification', checked)}
                />
              </div>

              {/* Automated conversation bot: same gate as legacy Agent.js (chatbotProviders from workflow settings). */}
              {isSysadmin && chatbotProvidersList.length > 0 && (
                <div className="form-section">
                  <div className="v5-section-header">{I('Automated Conversation Bot')}</div>

                  <V5Switch
                    id="automatedConversationBot"
                    label={I('Automated Conversation Bot')}
                    name="automatedConversationBot"
                    checked={conversationBotEnabled}
                    onChange={(checked) => handleCheckboxChange('automatedConversationBot', checked)}
                  />

                  {conversationBotEnabled && chatbotProvidersList.length > 0 && (
                    <V5Select
                      id="automatedConversationBotProvider"
                      label="Provider"
                      name="automatedConversationBotProvider"
                      placeholder="Select a provider"
                      value={
                        input.automatedConversationBotProvider > 0
                          ? input.automatedConversationBotProvider
                          : ''
                      }
                      onChange={handleSelectChange}
                      options={chatbotProvidersList.map((p) => ({
                        id: p.id,
                        name: p.name || p.Name || ''
                      }))}
                    />
                  )}
                </div>
              )}

              {isSysadmin && showAutoAnswerFeature && (
                <div className="form-section">
                  <div className="v5-section-header">{I('Automation')}</div>
                  <V5Switch
                    id="autoAnswer"
                    label={I('Auto Answer')}
                    name="autoAnswer"
                    checked={input.autoAnswer || false}
                    onChange={(checked) => handleCheckboxChange('autoAnswer', checked)}
                  />
                </div>
              )}

              {/* Quick Search Section */}
              <Feature tag="quicksearch.enabled">
                <div className="form-section">
                  <div className="v5-section-header">{I('Quick Search Settings')}</div>
                  
                  <V5Switch
                    id="doQuickSearch"
                    label={I('Enable Quick Search')}
                    name="doQuickSearch"
                    checked={input.doQuickSearch || false}
                    onChange={(checked) => handleCheckboxChange('doQuickSearch', checked)}
                  />
                  
                  {input.doQuickSearch && (
                    <V5Input
                      id="quickSearchAgentReferenceCode"
                      label={I('Quick Search Reference Code')}
                      name="quickSearchAgentReferenceCode"
                      value={input.quickSearchAgentReferenceCode || ''}
                      onChange={handleTextInputChange}
                      placeholder={I('Enter quick search reference code')}
                    />
                  )}
                </div>
              </Feature>

              {/* System Preferences Section */}
              <div className="form-section">
                <div className="v5-section-header">{I('System Preferences')}</div>

              <V5Select
                id="interfaceLanguage"
                  label={I('Language')}
                name="interfaceLanguage"
                value={input.interfaceLanguage || 1}
                onChange={handleSelectChange}
                options={interfaceLanguageOptions}
                />
                
                <V5Select
                  id="darkModeAppearance"
                  label={I('Dark Mode Appearance')}
                  name="darkModeAppearance"
                  value={input.darkModeAppearance || 0}
                  onChange={handleSelectChange}
                  options={DARK_MODE_APPEARANCE}
              />

                <Feature tag="open-external-system">
                  <V5Select
                    id="openExternalSystem"
                    label={I('Open external system')}
                    name="openExternalSystem"
                    value={input.openExternalSystem || 0}
                    onChange={handleSelectChange}
                    options={input.openExternalSystemOptions || [
                      { id: 0, name: I('Disabled') },
                      { id: 1, name: I('Manual (New tab)') },
                      { id: 2, name: I('Auto (New tab)') },
                      { id: 3, name: I('Auto (New window)') }
                    ]}
                  />
                </Feature>

              <Feature tag="show-time-zone">
                <V5Select
                  id="timezoneID"
                    label={I('Timezone')}
                  name="timezoneID"
                  value={input.timezoneID || 14}
                  onChange={handleSelectChange}
                  options={formatValueToNameObj(timeZone)}
                />
              </Feature>

              <V5Input
                  id="allowedIP"
                  label={I('Allowed IP')}
                  name="allowedIP"
                  value={input.allowedIP || ''}
                onChange={handleTextInputChange}
                  placeholder={I('Enter allowed IP addresses (comma separated)')}
                  helperText={I('Leave empty to allow all IPs')}
              />

              <V5Switch
                  id="shortHistory"
                  label={I('Sort History Ascending')}
                  name="shortHistory"
                  checked={input.shortHistory || false}
                  onChange={(checked) => handleCheckboxChange('shortHistory', checked)}
              />

                {!isNew && (
                  <div className="access-key-section">
                    <div className="v5-section-header">{I('Access Keys')}</div>
  
                    {accessKeyData && accessKeyURLCreator && onCreateAgentAccessKey && onDeleteAgentAccessKey && onLoadAgentAccessKeys ? (
                      <AgentAccessKey
                        agentID={activeId}
                        accessKeyURLCreator={accessKeyURLCreator}
                        data={accessKeyData}
                        onCreate={onCreateAgentAccessKey}
                        onDelete={onDeleteAgentAccessKey}
                        onLoad={onLoadAgentAccessKeys}
                        timezoneOffset={0}
                      />
                    ) : (
                      <div className="debug-error-message">
                        <strong>Access Key Component Not Loading:</strong><br/>
                        accessKeyData: {accessKeyData ? '✅' : '❌'}<br/>
                        accessKeyURLCreator: {accessKeyURLCreator ? '✅' : '❌'}<br/>
                        onCreateAgentAccessKey: {onCreateAgentAccessKey ? '✅' : '❌'}<br/>
                        onDeleteAgentAccessKey: {onDeleteAgentAccessKey ? '✅' : '❌'}<br/>
                        onLoadAgentAccessKeys: {onLoadAgentAccessKeys ? '✅' : '❌'}<br/>
                        activeId: {activeId}<br/>
                        isNew: {isNew ? 'true' : 'false'}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </TabPane>
        </TabContent>
      </div>

      {/* Action Buttons */}
      <div className='agent-actions'>
        {!isNew && (
          <>
            <Button
              title={I("Remove agent")}
              className={'btn-text text-normal'}
              onClick={() => onDelete(activeId)}
            >
              <i className="icon-remove"></i>
              {I("Remove")}
            </Button>
            {onActivateAgent && (
              <Button
                title={input.active ? I("Deactivate agent") : I("Activate agent")}
                className={'btn-text text-danger'}
                onClick={() => onActivateAgent(!input.active, activeId)}
              >
                <i className={input.active ? "icon-trash" : "icon-check"}></i>
                {input.active ? I("Deactivate agent") : I("Activate agent")}
              </Button>
            )}
          </>
        )}
      </div>

      <EditorFooter hidden={false}>
        <SaveButton
          onClick={handleSave}
          saving={adminStatus === 'saving'}
          text={I('Save')}
        />
        <CancelButton
          onClick={onCancel}
          text={I('Cancel')}
        />
      </EditorFooter>
      </form>
      </div>
    </div>
  );
};

export default AgentEditForm; 